// ESRI Shapefile (.shp) byte layout — mixed endianness is the format's own
// well-known gotcha. Main header (100 bytes): bytes 0-3 file code, BIG-endian;
// bytes 24-27 file length, BIG-endian; bytes 28-35 version/shape type,
// little-endian; bytes 36-99 bbox, little-endian. Per record (from offset
// 100): 8-byte header (record number BIG-endian, content length in 16-bit
// words BIG-endian), then little-endian content — Point: shape type + X/Y
// doubles; PolyLine/Polygon: shape type, bbox, NumParts, NumPoints, Parts
// (start index per part), Points (flat [X,Y] array).
//
// Ring winding order (shapefile's exterior-CW/hole-CCW, opposite of
// GeoJSON's) is deliberately NOT normalized — nothing downstream reads past
// ring [0] or checks winding.

type ShpShapeTypeCode = 1 | 3 | 5;

interface ShpRecord {
  recordNumber: number;
  /** One entry per part; a Point record always has exactly one part with exactly one point. */
  parts: [number, number][][];
}

interface ParsedShp {
  shapeType: ShpShapeTypeCode;
  records: ShpRecord[];
}

function isSupportedShapeType(type: number): type is ShpShapeTypeCode {
  return type === 1 || type === 3 || type === 5;
}

export function parseShp(buffer: ArrayBuffer): ParsedShp {
  try {
    return parseShpUnchecked(buffer);
  } catch (err) {
    // `DataView`/`TypedArray` reads are bounds-checked and throw a native
    // `RangeError` the instant a corrupt/truncated file's own declared
    // lengths would read past the buffer — that's already a clean FAILURE
    // (never silently reads garbage past the end), just not a clear one:
    // re-thrown here as a real, descriptive error instead of leaking that
    // native exception straight to the UI.
    if (err instanceof RangeError) {
      throw new Error("Malformed shapefile: unexpected end of file");
    }
    throw err;
  }
}

function parseShpUnchecked(buffer: ArrayBuffer): ParsedShp {
  const view = new DataView(buffer);
  const shapeType = view.getInt32(32, true);
  if (!isSupportedShapeType(shapeType)) {
    throw new Error(`Unsupported shapefile shape type ${shapeType}`);
  }

  const records: ShpRecord[] = [];
  let offset = 100;
  while (offset < buffer.byteLength) {
    const recordNumber = view.getInt32(offset, false);
    const contentLengthWords = view.getInt32(offset + 4, false);
    // Must reject: zero/negative content length never advances the read
    // cursor, spinning this loop forever.
    if (contentLengthWords <= 0) {
      throw new Error(
        `Malformed shapefile: non-positive record content length (${contentLengthWords}) at byte offset ${offset}`
      );
    }
    const contentStart = offset + 8;
    const recordShapeType = view.getInt32(contentStart, true);

    let parts: [number, number][][];
    if (recordShapeType === 1) {
      const x = view.getFloat64(contentStart + 4, true);
      const y = view.getFloat64(contentStart + 12, true);
      parts = [[[x, y]]];
    } else if (recordShapeType === 3 || recordShapeType === 5) {
      const numParts = view.getInt32(contentStart + 36, true);
      const numPoints = view.getInt32(contentStart + 40, true);
      const partsStart = contentStart + 44;
      const pointsStart = partsStart + 4 * numParts;
      const partIndices: number[] = [];
      for (let i = 0; i < numParts; i++) {
        partIndices.push(view.getInt32(partsStart + 4 * i, true));
      }
      const allPoints: [number, number][] = [];
      for (let i = 0; i < numPoints; i++) {
        const px = view.getFloat64(pointsStart + 16 * i, true);
        const py = view.getFloat64(pointsStart + 16 * i + 8, true);
        allPoints.push([px, py]);
      }
      parts = partIndices.map((start, i) => {
        const end = i + 1 < partIndices.length ? partIndices[i + 1] : numPoints;
        return allPoints.slice(start, end);
      });
    } else if (recordShapeType === 0) {
      parts = []; // Null shape — a real, if empty, record; skip its geometry.
    } else {
      throw new Error(`Unsupported shapefile shape type ${recordShapeType}`);
    }

    records.push({ recordNumber, parts });
    const nextOffset = contentStart + contentLengthWords * 2;
    // The read cursor must always advance, or this loop never terminates.
    if (nextOffset <= offset) {
      throw new Error(
        `Malformed shapefile: record at byte offset ${offset} does not advance (next offset ${nextOffset})`
      );
    }
    offset = nextOffset;
  }

  return { shapeType, records };
}
